import { useEffect, useState } from "react";
import { Zap, Loader2, X } from "lucide-react";
import api, { formatApiError } from "../../../lib/api";
import SignaturePad from "../../SignaturePad";
import DamageInspector from "./DamageInspector";
import { isoToEU } from "../../../lib/format";

const FUEL_LEVELS = ["8/8", "7/8", "6/8", "4/8"];

/** v4.6.18 — Vehicle Handover Confirmation (Express Check-In desk action). */
export default function HandoverModal({ booking, onClose, onDone }) {
  const [odometer, setOdometer] = useState("");
  const [fuel, setFuel] = useState("8/8");
  const [notes, setNotes] = useState("");
  const [plates, setPlates] = useState([]);
  const [plateId, setPlateId] = useState("");
  const [damages, setDamages] = useState([]);
  const [signature, setSignature] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const v = booking?.vehicle_snapshot || {};

  // v4.7.3 — Escape closes the modal.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // v4.7.1 — which physical units of this car group the desk can hand out.
  useEffect(() => {
    if (!booking?.id) return;
    let cancelled = false;
    api.get(`/admin/bookings/${booking.id}/available-plates`)
      .then((r) => {
        if (cancelled) return;
        const list = r?.data?.plates || [];
        setPlates(list);
        const preselect = r?.data?.assigned_vehicle_id || (list.length === 1 ? list[0].id : "");
        setPlateId(preselect || "");
        const km = Number(list.find((p) => p.id === preselect)?.mileage_km || 0);
        if (km > 0) setOdometer(String(km));
      })
      .catch(() => { /* group may not track individual plates */ });
    return () => { cancelled = true; };
  }, [booking?.id]);

  useEffect(() => {
    if (!booking?.vehicle_id) return;
    let cancelled = false;
    api.get(`/vehicles/${booking.vehicle_id}`)
      .then((r) => {
        const km = Number(r?.data?.current_odometer || 0);
        if (!cancelled && km > 0) setOdometer(String(km));
      })
      .catch(() => { /* prefill is best-effort */ });
    return () => { cancelled = true; };
  }, [booking?.vehicle_id]);

  const submit = async () => {
    if (plates.length > 0 && !plateId) {
      setError("Please assign a physical vehicle (plate) before handing over.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/admin/bookings/${booking.id}/handover`, {
        odometer: odometer === "" ? null : Number(odometer),
        fuel_level: fuel,
        notes,
        assigned_vehicle_id: plateId || null,
        damages,
        signature,
      });
      onDone?.(data.booking);
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
      data-testid="handover-modal"
    >
      <div
        className="bg-white dark:bg-[#1F2937] text-[#0A0A0A] dark:text-[#F9FAFB] w-full max-w-lg rounded border border-black/10 dark:border-white/10 p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 dark:text-neutral-400">
              Express check-in
            </p>
            <h2 className="font-display text-2xl mt-1">Vehicle handover confirmation</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 p-1.5 -mr-1 -mt-1 rounded hover:bg-black/5 dark:hover:bg-white/10 text-neutral-500 hover:text-neutral-900 dark:hover:text-white transition-colors"
            data-testid="handover-close"
          >
            <X size={18} />
          </button>
        </div>

        <dl className="mt-5 grid grid-cols-2 gap-x-5 gap-y-3 text-sm" data-testid="handover-summary">
          <div>
            <dt className="text-[10px] uppercase tracking-widest text-neutral-500">Driver</dt>
            <dd className="font-semibold">{booking.full_name || "—"}</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase tracking-widest text-neutral-500">Reference</dt>
            <dd className="font-mono">{(booking.booking_reference || booking.id || "").slice(0, 8).toUpperCase()}</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase tracking-widest text-neutral-500">Vehicle</dt>
            <dd className="font-semibold">
              {[v.brand, v.name].filter(Boolean).join(" ") || "—"}
              {v.category ? <span className="text-neutral-500 font-normal"> · {v.category}</span> : null}
            </dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase tracking-widest text-neutral-500">Plate</dt>
            <dd className="font-mono uppercase">{v.license_plate || booking.license_plate || "—"}</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase tracking-widest text-neutral-500">Pickup</dt>
            <dd>{isoToEU(booking.pickup_date)} · {booking.pickup_time || "—"}</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase tracking-widest text-neutral-500">Return</dt>
            <dd>{isoToEU(booking.return_date)} · {booking.return_time || "—"}</dd>
          </div>
        </dl>

        <div className="mt-6">
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-neutral-500">
              Assign physical vehicle (plate){plates.length > 0 ? " *" : ""}
            </span>
            <select
              value={plateId}
              onChange={(e) => {
                setPlateId(e.target.value);
                const km = Number(plates.find((p) => p.id === e.target.value)?.mileage_km || 0);
                if (km > 0) setOdometer(String(km));
              }}
              disabled={plates.length === 0}
              className="mt-1 w-full bg-transparent border border-black/15 dark:border-white/15 rounded px-3 py-2 text-sm outline-none disabled:opacity-60"
              data-testid="handover-plate"
            >
              <option value="">
                {plates.length === 0 ? "No plates registered for this car group" : "Select a plate…"}
              </option>
              {plates.map((p) => (
                <option key={p.id} value={p.id} className="text-black">
                  {p.license_plate}{p.mileage_km ? ` · ${Number(p.mileage_km).toLocaleString()} km` : ""}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-4">
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-neutral-500">Odometer (km)</span>
            <input
              type="number"
              min="0"
              value={odometer}
              onChange={(e) => setOdometer(e.target.value)}
              placeholder="e.g. 48250"
              className="mt-1 w-full bg-transparent border border-black/15 dark:border-white/15 rounded px-3 py-2 text-sm outline-none focus:border-black dark:focus:border-white"
              data-testid="handover-odometer"
            />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-neutral-500">Fuel level</span>
            <select
              value={fuel}
              onChange={(e) => setFuel(e.target.value)}
              className="mt-1 w-full bg-transparent border border-black/15 dark:border-white/15 rounded px-3 py-2 text-sm outline-none"
              data-testid="handover-fuel"
            >
              {FUEL_LEVELS.map((f) => (
                <option key={f} value={f} className="text-black">{f}</option>
              ))}
            </select>
          </label>
        </div>

        <label className="block mt-4">
          <span className="text-[10px] uppercase tracking-widest text-neutral-500">
            Handover notes / existing damage
          </span>
          <textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Scratch on rear bumper, spare tyre present…"
            className="mt-1 w-full bg-transparent border border-black/15 dark:border-white/15 rounded px-3 py-2 text-sm outline-none focus:border-black dark:focus:border-white"
            data-testid="handover-notes"
          />
        </label>

        <div className="mt-5">
          <DamageInspector damages={damages} onChange={setDamages} testId="handover-damage" />
        </div>

        <div className="mt-5">
          <p className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Customer signature</p>
          <SignaturePad onChange={setSignature} height={140} />
        </div>

        {error && (
          <p className="text-sm text-rose-600 dark:text-rose-400 mt-4" data-testid="handover-error">{error}</p>
        )}

        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="border border-black/15 dark:border-white/15 px-4 py-2 text-sm rounded hover:border-black dark:hover:border-white"
            data-testid="handover-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            className="bg-sky-600 text-white px-4 py-2 text-sm rounded inline-flex items-center gap-2 hover:bg-sky-700 disabled:opacity-50 transition-colors"
            data-testid="handover-confirm"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
            Confirm handover &amp; deliver vehicle
          </button>
        </div>
      </div>
    </div>
  );
}
